import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientSentimentRequest, ClientSentimentResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SampleSizeNoteComponent } from '../../../../shared/components/sette/sample-size-note/sample-size-note.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { LowDataBadgeComponent } from '../../../../shared/components/sette/low-data-badge/low-data-badge.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, isCompareRequested, resolveComparison, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, noMentionsLabel, isNewMetric, newMetricLabel, isLowDataCount } from '../shared/analytics-request.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsDate, formatAnalyticsAxisDate, sentimentScaleIndex, SENTIMENT_COLORS, AXIS, Tone } from '../../../../shared/components/sette/chart-theme';
import { translateTopic } from '../shared/taxonomy.util';

interface BrandSentimentRowView {
	brand: string;
	mentions: number;
	posPct: number;
	neuPct: number;
	negPct: number;
	// True only when there is genuinely nothing to split (no impact-scored mentions at all) - the
	// bar has no real numbers to show. Never true merely because `mentions` is small; the split is
	// computed from raw positive/neutral/negativeCount, which the backend never gates by mention
	// count (CLIPPING-threshold, 2026-10-25 - see analytics-request.util.ts).
	noData: boolean;
	// Styling-only: bar/value still shown in full, just paled + a small badge (isLowDataCount).
	lowData: boolean;
	client: boolean;
}

interface BrandNetSentimentRowView {
	brand: string;
	mentions: number;
	netScoreStr: string;
	netScore: number;
	lowData: boolean;
	client: boolean;
}

interface TopicSentimentRowView {
	topic: string;
	mentions: number;
	pos: string;
	neu: string;
	neg: string;
	net: string;
	netTone: Tone;
	noData: boolean;
	lowData: boolean;
}

interface MediaSentimentRowView {
	type: string;
	mentions: number;
	posPct: number;
	neuPct: number;
	negPct: number;
	net: string;
	netTone: Tone;
	noData: boolean;
	lowData: boolean;
}

interface NegativeDriverRowView {
	issue: string;
	negCount: string;
	mentions: string;
}

interface HeatmapCellView {
	text: string;
	raw: number | null;
}

interface HeatmapRowView {
	topic: string;
	cells: Record<string, HeatmapCellView>;
}

interface NegativeMentionView {
	id: string;
	brand: string;
	isClient: boolean;
	media: string;
	mediaType: string;
	headline: string;
	brief: string;
	sentiment: string;
	impactScore: string;
	reachScore: number;
	dateLabel: string;
}

interface SentimentShiftRowView {
	name: string;
	type: string;
	badge: string;
	details: string;
	improved: boolean;
}

const MEDIA_TYPE_LABEL_KEYS: Record<string, string> = {
	WEB: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_WEB',
	TV: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_TV',
	PRINTED: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_PRINT',
	RADIO: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_RADIO',
	SOCIAL: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_SOCIAL',
};

// Ported from analytics-design/src/components/SentimentDashboard.tsx, then rewired to call
// POST /v1/client/analytics/sentiment instead of using sample data. The design's own inline
// date-range/Filters/Export header row is not ported (same reasoning as before: the shared
// FilterBar already covers it). KPI deltas are only shown where the backend tracks a previous
// value (Net Sentiment, via previousNetIndex) - Positive/Neutral/Negative %/count cards don't
// have a tracked previous split, so they show the current count only, no trend arrow. "Negative
// Sentiment Drivers" is now topics ranked by absolute negative mention count (drops the "vs prev"
// column and the free-text "issue" framing - those need data this backend doesn't track at topic
// granularity). The Topic x Brand heatmap now has dynamic columns (real selected brands, not a
// fixed 6) and reads avgImpact per cell instead of a separately-tracked "net sentiment" per cell.
@Component({
	selector: 'app-sentiment',
	standalone: true,
	imports: [MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SampleSizeNoteComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent, LowDataBadgeComponent],
	templateUrl: './sentiment.component.html',
	styleUrl: './sentiment.component.scss',
})
export class SentimentComponent implements OnChanges {
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	data: ClientSentimentResponse | null = null;

	showManageModal = false;
	private readonly fetchTrigger = new Subject<ClientSentimentRequest>();

	constructor(private readonly api: ClientAnalyticsControllerService, private readonly utils: UtilsService, private readonly translate: TranslateService) {
		// Задача 7: switchMap-based stale-response guard - see createAnalyticsFetchStream's doc.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getSentimentAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.data = result.data!;
				} else {
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	get lang(): string {
		return this.utils.currentLanguage;
	}

	fmtInt(n: number): string {
		return formatAnalyticsInt(n, this.lang);
	}

	fmt1(n: number): string {
		return formatAnalyticsNumber(n, this.lang, 1);
	}

	get showSkeleton(): boolean {
		return this.loading && !this.data;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.data;
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.data?.summary?.mentions ?? 0) === 0;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetch();
		}
	}

	fetch(): void {
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(fields as ClientSentimentRequest);
	}

	openManageModal(): void {
		this.showManageModal = true;
	}

	closeManageModal(): void {
		this.showManageModal = false;
	}

	addCompetitorBrand(): void {
		window.alert('Brand configuration options are saved.');
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	mediaTypeLabel(code: string): string {
		const key = MEDIA_TYPE_LABEL_KEYS[code];
		return key ? this.translate.instant(key) : code;
	}

	// --- Header badge + KPI row ---

	get netSentimentIndexLabel(): string {
		const v = this.data?.summary?.netIndex;
		return v != null ? formatSigned(v, 1, this.lang) : '—';
	}

	get netSentimentIndexTone(): Tone {
		return signTone(this.data?.summary?.netIndex ?? 0);
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	toneTextClass(tone: Tone): string {
		if (tone === 'positive') return 'text-positive';
		if (tone === 'negative') return 'text-negative';
		return 'text-ink-subtle';
	}

	get summaryMentions(): number {
		return this.data?.summary?.mentions ?? 0;
	}

	get positivePct(): string {
		const v = this.data?.summary?.positivePercent;
		return v != null ? `${formatAnalyticsNumber(v, this.lang, 1)}%` : '—';
	}

	get neutralPct(): string {
		const v = this.data?.summary?.neutralPercent;
		return v != null ? `${formatAnalyticsNumber(v, this.lang, 1)}%` : '—';
	}

	get negativePct(): string {
		const v = this.data?.summary?.negativePercent;
		return v != null ? `${formatAnalyticsNumber(v, this.lang, 1)}%` : '—';
	}

	get netSentimentKpi(): { value: string; trendUp?: boolean; deltaLabel: string } {
		const v = this.data?.summary?.netIndex;
		if (v == null) return { value: '—', deltaLabel: noMentionsLabel(this.translate) };
		const value = formatSigned(v, 1, this.lang);
		const comparison = resolveComparison(isCompareRequested(this.filters), this.data?.summary?.previousNetIndex, this.data?.summary?.hasEnoughCompareData);
		if (comparison.insufficient) {
			return { value, deltaLabel: isNewMetric(v, 0) ? newMetricLabel(this.translate) : '' };
		}
		if (!comparison.show) {
			return { value, deltaLabel: `(${this.positivePct} − ${this.negativePct})` };
		}
		const prev = comparison.previous as number;
		const delta = v - prev;
		return { value, trendUp: v >= prev, deltaLabel: this.translate.instant('ANALYTICS_SENTIMENT.DELTA_VS_COMPARISON', { delta: formatSigned(delta, 1, this.lang) }) };
	}

	// Positive/Neutral/Negative % deltas vs the compare period, same shared comparison rule as
	// every other delta in the Analytics screens (no stored previous period otherwise). Falls
	// back to the existing count-based caption whenever there's nothing to compare (no compare
	// period requested, or the current period itself is below the floor). `badWhenUp` flips the
	// color (not the arrow direction - trendUp still reflects the real direction) for Negative%,
	// where an increase is bad news, same deltaTone semantics used everywhere else.
	private percentKpi(
		value: number | undefined,
		previous: number | undefined | null,
		hasEnoughCompareData: boolean | undefined,
		fallbackLabel: string,
		badWhenUp = false
	): { value: string; trendUp?: boolean; deltaLabel: string; trendColorClass?: string } {
		const display = value != null ? `${formatAnalyticsNumber(value, this.lang, 1)}%` : '—';
		if (value == null) {
			return { value: display, deltaLabel: fallbackLabel };
		}
		const comparison = resolveComparison(isCompareRequested(this.filters), previous, hasEnoughCompareData);
		if (comparison.insufficient) {
			return { value: display, deltaLabel: isNewMetric(value, 0) ? newMetricLabel(this.translate) : fallbackLabel };
		}
		if (!comparison.show) {
			return { value: display, deltaLabel: fallbackLabel };
		}
		const prev = comparison.previous as number;
		const delta = value - prev;
		const trendUp = value >= prev;
		const trendColorClass = badWhenUp ? (trendUp ? 'text-negative' : 'text-positive') : undefined;
		return { value: display, trendUp, deltaLabel: this.translate.instant('ANALYTICS_SENTIMENT.DELTA_VS_COMPARISON', { delta: formatSigned(delta, 1, this.lang) }), trendColorClass };
	}

	get positiveKpi() {
		return this.percentKpi(
			this.data?.summary?.positivePercent,
			this.data?.summary?.previousPositivePercent,
			this.data?.summary?.hasEnoughCompareData,
			this.translate.instant('ANALYTICS_SENTIMENT.POSITIVE_STORIES_COUNT', { count: formatAnalyticsInt(this.positiveCount, this.lang) })
		);
	}

	get neutralKpi() {
		return this.percentKpi(
			this.data?.summary?.neutralPercent,
			this.data?.summary?.previousNeutralPercent,
			this.data?.summary?.hasEnoughCompareData,
			this.translate.instant('ANALYTICS_SENTIMENT.STANDARD_NOTICES_COUNT', { count: formatAnalyticsInt(this.neutralCount, this.lang) })
		);
	}

	get negativeKpi() {
		return this.percentKpi(
			this.data?.summary?.negativePercent,
			this.data?.summary?.previousNegativePercent,
			this.data?.summary?.hasEnoughCompareData,
			this.translate.instant('ANALYTICS_SENTIMENT.CRITICAL_MENTIONS_COUNT', { count: formatAnalyticsInt(this.negativeCount, this.lang) }),
			true
		);
	}

	private countOf(pct?: number): number {
		return pct != null ? Math.round((pct / 100) * this.summaryMentions) : 0;
	}

	get positiveCount(): number {
		return this.countOf(this.data?.summary?.positivePercent);
	}

	get neutralCount(): number {
		return this.countOf(this.data?.summary?.neutralPercent);
	}

	get negativeCount(): number {
		return this.countOf(this.data?.summary?.negativePercent);
	}

	// --- Widget 1: Sentiment trend ---

	get sentimentTrendChartData() {
		const trend = this.data?.trend ?? [];
		const labels = trend.map((t) => (t.bucketStart ? formatAnalyticsAxisDate(new Date(t.bucketStart), this.lang) : ''));
		return {
			labels,
			datasets: [
				{ label: `${this.translate.instant('ANALYTICS_GLOSSARY.POSITIVE')} %`, data: trend.map((t) => t.positivePercent ?? null), borderColor: SENTIMENT_COLORS.positive, backgroundColor: 'transparent', borderWidth: 2.2, tension: 0.35, pointRadius: 3, pointBackgroundColor: SENTIMENT_COLORS.positive, fill: false, spanGaps: true },
				{ label: `${this.translate.instant('ANALYTICS_GLOSSARY.NEUTRAL')} %`, data: trend.map((t) => t.neutralPercent ?? null), borderColor: SENTIMENT_COLORS.neutral, backgroundColor: 'transparent', borderWidth: 1.8, tension: 0.35, pointRadius: 2.5, pointBackgroundColor: SENTIMENT_COLORS.neutral, fill: false, spanGaps: true },
				{ label: `${this.translate.instant('ANALYTICS_GLOSSARY.NEGATIVE')} %`, data: trend.map((t) => t.negativePercent ?? null), borderColor: SENTIMENT_COLORS.negative, backgroundColor: 'transparent', borderWidth: 1.8, tension: 0.35, pointRadius: 2.5, pointBackgroundColor: SENTIMENT_COLORS.negative, fill: false, spanGaps: true },
				{ label: this.translate.instant('ANALYTICS_SENTIMENT.NET_SENTIMENT_INDEX_LEGEND'), data: trend.map((t) => t.netIndex ?? null), borderColor: '#244896', backgroundColor: 'transparent', borderWidth: 2, borderDash: [4, 4], tension: 0.35, pointRadius: 3, pointBackgroundColor: '#244896', fill: false, spanGaps: true },
			],
		};
	}

	readonly sentimentTrendChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: 'index' as const, intersect: false },
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				bodyFont: { size: 10 },
				titleFont: { size: 10, weight: 'bold' as const },
				callbacks: { label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.datasetIndex === 3 ? formatSignedAuto(ctx.parsed.y, this.lang) + ' pts' : formatAnalyticsNumber(ctx.parsed.y, this.lang, 1) + '%'}` },
			},
		},
		scales: { x: { grid: { display: false }, ticks: { font: { size: 10 }, color: AXIS.text } }, y: { grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text } } },
	};

	// --- Widget 2: Sentiment distribution ---

	get sentimentDistChartData() {
		const s = this.data?.summary;
		const labels = [this.translate.instant('ANALYTICS_GLOSSARY.POSITIVE'), this.translate.instant('ANALYTICS_GLOSSARY.NEUTRAL'), this.translate.instant('ANALYTICS_GLOSSARY.NEGATIVE')];
		return { labels, datasets: [{ data: [s?.positivePercent ?? 0, s?.neutralPercent ?? 0, s?.negativePercent ?? 0], backgroundColor: [SENTIMENT_COLORS.positive, SENTIMENT_COLORS.neutral, SENTIMENT_COLORS.negative], borderWidth: 2, borderColor: '#ffffff', hoverOffset: 4 }] };
	}

	readonly sentimentDistChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: '70%',
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (c: any) => ` ${c.label}: ${formatAnalyticsNumber(c.parsed, this.lang, 1)}%` } } },
	};

	// --- Widget 3: Sentiment by brand ---

	get brandSentimentData(): BrandSentimentRowView[] {
		return (this.data?.byBrand ?? []).map((b) => {
			const total = (b.positiveCount ?? 0) + (b.neutralCount ?? 0) + (b.negativeCount ?? 0);
			return {
				brand: b.name || '',
				mentions: b.mentions ?? 0,
				posPct: total > 0 ? Math.round(((b.positiveCount ?? 0) / total) * 100) : 0,
				neuPct: total > 0 ? Math.round(((b.neutralCount ?? 0) / total) * 100) : 0,
				negPct: total > 0 ? Math.round(((b.negativeCount ?? 0) / total) * 100) : 0,
				noData: total === 0,
				lowData: isLowDataCount(b.mentions),
				client: b.brandKind === 'COMPANY',
			};
		});
	}

	// --- Widget 4: Sentiment by topic ---

	get topTopicsSentiment(): TopicSentimentRowView[] {
		return (this.data?.byTopic ?? []).map((t) => ({
			topic: translateTopic(t.topic, this.translate),
			mentions: t.mentions ?? 0,
			pos: t.positivePercent != null ? `${formatAnalyticsNumber(t.positivePercent, this.lang, 1)}%` : '—',
			neu: t.neutralPercent != null ? `${formatAnalyticsNumber(t.neutralPercent, this.lang, 1)}%` : '—',
			neg: t.negativePercent != null ? `${formatAnalyticsNumber(t.negativePercent, this.lang, 1)}%` : '—',
			net: t.positivePercent != null && t.negativePercent != null ? formatSigned(t.positivePercent - t.negativePercent, 1, this.lang) : '—',
			netTone: t.positivePercent != null && t.negativePercent != null ? signTone(t.positivePercent - t.negativePercent) : 'neutral',
			noData: t.positivePercent == null,
			lowData: isLowDataCount(t.mentions),
		}));
	}

	// --- Widget 5: Net sentiment by brand ---

	get brandNetSentimentData(): BrandNetSentimentRowView[] {
		return (this.data?.byBrand ?? []).map((b) => ({
			brand: b.name || '',
			mentions: b.mentions ?? 0,
			netScore: b.netSentimentPercent ?? 0,
			netScoreStr: b.netSentimentPercent != null ? formatSigned(b.netSentimentPercent, 1, this.lang) : '—',
			lowData: !b.hasEnoughData,
			client: b.brandKind === 'COMPANY',
		}));
	}

	barWidthPct(netScore: number): number {
		return Math.min(100, Math.max(5, (Math.abs(netScore) / 65) * 100));
	}

	// SETTE redesign (CLIPPING-598), Фаза 6 fix from the design doc: this list had its own 4-tier
	// threshold with a blue step at +20..+39 (a Принцип 1 violation - blue is reserved for
	// action/selection). Replaced with the shared symmetric 5-level sentiment scale.
	private netScoreScaleIndex(netScore: number): number {
		return sentimentScaleIndex(netScore, 40);
	}

	private static readonly SCALE_BAR_CLASSES = ['bg-positive', 'bg-positive-soft', 'bg-neutral-bar', 'bg-negative-soft', 'bg-negative'];
	private static readonly SCALE_BADGE_CLASSES = ['bg-positive-tint text-positive', 'bg-positive-tint text-positive', 'bg-neutral-tint text-ink-subtle', 'bg-negative-tint text-negative', 'bg-negative-tint text-negative'];

	netScoreBarClass(netScore: number): string {
		return SentimentComponent.SCALE_BAR_CLASSES[this.netScoreScaleIndex(netScore)];
	}

	netScoreBadgeClass(netScore: number): string {
		return SentimentComponent.SCALE_BADGE_CLASSES[this.netScoreScaleIndex(netScore)];
	}

	// --- Widget 6: Sentiment by media type ---

	get mediaSentimentData(): MediaSentimentRowView[] {
		return (this.data?.byMediaType ?? []).map((m) => ({
			type: this.mediaTypeLabel(m.type || ''),
			mentions: m.mentions ?? 0,
			posPct: Math.round(m.positivePercent ?? 0),
			neuPct: Math.round(m.neutralPercent ?? 0),
			negPct: Math.round(m.negativePercent ?? 0),
			net: m.positivePercent != null && m.negativePercent != null ? formatSigned(m.positivePercent - m.negativePercent, 1, this.lang) : '—',
			netTone: m.positivePercent != null && m.negativePercent != null ? signTone(m.positivePercent - m.negativePercent) : 'neutral',
			noData: m.positivePercent == null,
			lowData: isLowDataCount(m.mentions),
		}));
	}

	// --- Widget 7: Negative sentiment drivers (topics ranked by absolute negative count) ---

	get negativeDrivers(): NegativeDriverRowView[] {
		return (this.data?.byTopic ?? [])
			.filter((t) => t.hasEnoughData && t.negativePercent != null)
			.map((t) => ({ topic: translateTopic(t.topic, this.translate), negCount: Math.round(((t.negativePercent ?? 0) / 100) * (t.mentions ?? 0)), mentions: t.mentions ?? 0 }))
			.sort((a, b) => b.negCount - a.negCount)
			.slice(0, 4)
			.map((r) => ({ issue: r.topic, negCount: formatAnalyticsInt(r.negCount, this.lang), mentions: formatAnalyticsInt(r.mentions, this.lang) }));
	}

	// --- Widget 8: Topic x brand heatmap ---

	get heatmapColumns(): { key: string; label: string }[] {
		return (this.data?.topicByBrandHeatmap?.columns ?? []).map((c) => ({ key: c.key || '', label: c.label || c.key || '' }));
	}

	get heatmapRows(): HeatmapRowView[] {
		return (this.data?.topicByBrandHeatmap?.rows ?? []).slice(0, 10).map((r) => {
			const cells: Record<string, HeatmapCellView> = {};
			for (const col of this.heatmapColumns) {
				const cell = r.cellsByColumnKey?.[col.key];
				const raw = cell?.avgImpact != null ? Math.round(cell.avgImpact) : null;
				cells[col.key] = { text: raw != null ? formatSignedAuto(raw, this.lang) : '—', raw };
			}
			return { topic: translateTopic(r.rowLabel || r.rowKey, this.translate), cells };
		});
	}

	// SETTE redesign (CLIPPING-598), Правило Г: this is a sentiment/impact heatmap, so it gets
	// the same symmetric 5-level sentiment scale as every other sentiment display, not an ad-hoc
	// 7-tier emerald/rose scale.
	getHeatmapColor(raw: number | null): string {
		if (raw == null) return 'bg-page text-ink-subtle';
		const idx = sentimentScaleIndex(raw, 40);
		const bg = ['bg-positive', 'bg-positive-soft', 'bg-neutral-bar', 'bg-negative-soft', 'bg-negative'];
		const text = idx === 0 || idx === 4 ? 'text-on-blue' : 'text-ink';
		return `${bg[idx]} ${text} font-medium`;
	}

	// --- Widget 9: Recent highly negative mentions ---

	get recentNegativeMentions(): NegativeMentionView[] {
		return (this.data?.recentNegativeMentions ?? []).map((m) => ({
			id: m.clipId || '',
			brand: m.brandTagName || '',
			isClient: this.brandOptions.find((b) => b.name === m.brandTagName)?.brandKind === 'COMPANY',
			media: m.outletName || '',
			mediaType: this.mediaTypeLabel(m.outletType || ''),
			headline: m.headline || '',
			brief: m.brief || '',
			sentiment: this.translate.instant('ANALYTICS_GLOSSARY.NEGATIVE'),
			impactScore: formatSignedAuto(m.impact ?? 0, this.lang),
			reachScore: m.reach ?? 0,
			dateLabel: m.publicationDate ? formatAnalyticsDate(new Date(m.publicationDate), this.lang) : '',
		}));
	}

	// --- Widget 10: Sentiment shifts ---

	get sentimentShiftsData(): SentimentShiftRowView[] {
		return (this.data?.sentimentShifts ?? []).map((c) => ({
			name: c.type === 'topic_sentiment' ? translateTopic(c.headline, this.translate) : c.headline || '',
			type: c.type === 'topic_sentiment' ? this.translate.instant('ANALYTICS_GLOSSARY.TOPIC') : this.translate.instant('ANALYTICS_SENTIMENT.OUTLET_TYPE'),
			badge: c.badge || '',
			details: c.details || '',
			improved: !(c.badge || '').trim().startsWith('-'),
		}));
	}

	// Пакет А #9: sentimentShifts comes back empty both when no compare period was requested and
	// when one was requested but there simply were no notable shifts to report - the template used
	// to show "select a comparison period" for both, which is wrong (and confusing) once a period
	// is already selected. Analytics accuracy review follow-up (2026-11-01): no longer an
	// "insufficient comparison" message either (that implied a hidden value, which no longer
	// happens) - a plain "nothing notable this period" wording covers both the genuinely-empty
	// compare period and the no-notable-shifts case.
	get sentimentShiftsEmptyMessage(): string {
		return isCompareRequested(this.filters) ? this.translate.instant('ANALYTICS_SENTIMENT.NO_SHIFTS_WITH_COMPARE') : this.translate.instant('ANALYTICS_SENTIMENT.SELECT_COMPARISON_PERIOD');
	}
}
